const PHOTO_CROP_UPLOAD_PATCH = Symbol.for('eInvitation.photoAlbumCropUpload.v2')

const OUTPUTS = {
  square: { width: 1200, height: 1200, label: 'Square' },
  portrait: { width: 1200, height: 1600, label: 'Portrait' },
  landscape: { width: 1600, height: 900, label: 'Landscape' },
}

function isPhotoUploadInput(input) {
  return input instanceof HTMLInputElement &&
    input.type === 'file' &&
    input.closest('.epa-upload-form') &&
    String(input.accept || '').toLowerCase().includes('image')
}

function fileKey(file) {
  return `${file.name}_${file.size}_${file.lastModified}`
}

function loadImageSource(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => resolve({ image, url })
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error(`Unable to open ${file.name || 'photo'}.`))
    }
    image.src = url
  })
}

function canvasToFile(canvas, originalFile, shape) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to create the cropped photo.'))
        return
      }

      const baseName = String(originalFile.name || 'photo').replace(/\.[^.]+$/, '') || 'photo'
      resolve(new File([blob], `${baseName}-${shape}.jpg`, {
        type: 'image/jpeg',
        lastModified: originalFile.lastModified || Date.now(),
      }))
    }, 'image/jpeg', 0.92)
  })
}

async function cropFile(file, settings) {
  const loaded = await loadImageSource(file)

  try {
    const sourceWidth = loaded.image.naturalWidth
    const sourceHeight = loaded.image.naturalHeight
    const output = OUTPUTS[settings.shape]
    const targetRatio = output.width / output.height
    const sourceRatio = sourceWidth / sourceHeight

    let cropWidth
    let cropHeight
    if (sourceRatio > targetRatio) {
      cropHeight = sourceHeight
      cropWidth = cropHeight * targetRatio
    } else {
      cropWidth = sourceWidth
      cropHeight = cropWidth / targetRatio
    }

    const zoom = Math.max(1, Math.min(3, Number(settings.zoom) || 1))
    cropWidth /= zoom
    cropHeight /= zoom

    const maxX = Math.max(0, sourceWidth - cropWidth)
    const maxY = Math.max(0, sourceHeight - cropHeight)
    const sourceX = maxX * (Math.max(0, Math.min(100, settings.positionX)) / 100)
    const sourceY = maxY * (Math.max(0, Math.min(100, settings.positionY)) / 100)

    const canvas = document.createElement('canvas')
    canvas.width = output.width
    canvas.height = output.height
    const context = canvas.getContext('2d', { alpha: false })
    if (!context) throw new Error('Crop canvas is unavailable.')

    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, output.width, output.height)
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'
    context.drawImage(
      loaded.image,
      sourceX,
      sourceY,
      cropWidth,
      cropHeight,
      0,
      0,
      output.width,
      output.height
    )

    return canvasToFile(canvas, file, settings.shape)
  } finally {
    URL.revokeObjectURL(loaded.url)
  }
}

function createCropDialog(file, index, total) {
  return new Promise(async (resolve, reject) => {
    let loaded
    try {
      loaded = await loadImageSource(file)
    } catch (error) {
      reject(error)
      return
    }

    const sourceRatio = loaded.image.naturalWidth / loaded.image.naturalHeight
    const state = {
      shape: sourceRatio >= 1.35 ? 'landscape' : sourceRatio <= 0.8 ? 'portrait' : 'square',
      zoom: 1,
      positionX: 50,
      positionY: 50,
    }

    const overlay = document.createElement('div')
    overlay.className = 'epa-crop-overlay'
    overlay.setAttribute('role', 'dialog')
    overlay.setAttribute('aria-modal', 'true')
    overlay.setAttribute('aria-label', `Crop photo ${index + 1} of ${total}`)

    overlay.innerHTML = `
      <section class="epa-crop-dialog">
        <header class="epa-crop-header">
          <div>
            <span>PHOTO ${index + 1} OF ${total}</span>
            <h2>Crop before upload</h2>
          </div>
          <button type="button" class="epa-crop-cancel" aria-label="Cancel cropping">×</button>
        </header>

        <div class="epa-crop-shapes" role="group" aria-label="Crop shape">
          <button type="button" data-crop-shape="square">Square</button>
          <button type="button" data-crop-shape="portrait">Portrait</button>
          <button type="button" data-crop-shape="landscape">Landscape</button>
        </div>

        <div class="epa-crop-stage">
          <div class="epa-crop-viewport">
            <img alt="Crop preview" draggable="false" />
          </div>
        </div>

        <div class="epa-crop-controls">
          <label>
            <span>Zoom</span>
            <input type="range" min="1" max="3" step="0.05" value="1" data-crop-control="zoom" />
          </label>
          <label>
            <span>Move left / right</span>
            <input type="range" min="0" max="100" step="1" value="50" data-crop-control="positionX" />
          </label>
          <label>
            <span>Move up / down</span>
            <input type="range" min="0" max="100" step="1" value="50" data-crop-control="positionY" />
          </label>
        </div>

        <footer class="epa-crop-actions">
          <button type="button" class="epa-crop-secondary">Cancel</button>
          <button type="button" class="epa-crop-apply">Apply crop</button>
        </footer>
      </section>
    `

    const viewport = overlay.querySelector('.epa-crop-viewport')
    const preview = overlay.querySelector('.epa-crop-viewport img')
    const shapeButtons = Array.from(overlay.querySelectorAll('[data-crop-shape]'))
    const controls = Array.from(overlay.querySelectorAll('[data-crop-control]'))
    const applyButton = overlay.querySelector('.epa-crop-apply')
    const cancelButtons = overlay.querySelectorAll('.epa-crop-cancel, .epa-crop-secondary')

    preview.src = loaded.url

    function render() {
      viewport.dataset.shape = state.shape
      preview.style.objectPosition = `${state.positionX}% ${state.positionY}%`
      preview.style.transform = `scale(${state.zoom})`
      shapeButtons.forEach((button) => {
        const selected = button.dataset.cropShape === state.shape
        button.classList.toggle('is-active', selected)
        button.setAttribute('aria-pressed', selected ? 'true' : 'false')
      })
    }

    function cleanup() {
      URL.revokeObjectURL(loaded.url)
      overlay.remove()
      document.body.classList.remove('epa-crop-open')
    }

    function cancel() {
      cleanup()
      resolve(null)
    }

    shapeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        state.shape = button.dataset.cropShape
        render()
      })
    })

    controls.forEach((control) => {
      control.addEventListener('input', () => {
        state[control.dataset.cropControl] = Number(control.value)
        render()
      })
    })

    cancelButtons.forEach((button) => button.addEventListener('click', cancel))
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) cancel()
    })

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') cancel()
    }
    window.addEventListener('keydown', handleKeyDown, { once: true })

    applyButton.addEventListener('click', async () => {
      applyButton.disabled = true
      applyButton.textContent = 'Cropping…'
      try {
        const cropped = await cropFile(file, state)
        window.removeEventListener('keydown', handleKeyDown)
        cleanup()
        resolve(cropped)
      } catch (error) {
        applyButton.disabled = false
        applyButton.textContent = 'Apply crop'
        reject(error)
      }
    })

    document.body.appendChild(overlay)
    document.body.classList.add('epa-crop-open')
    render()
    shapeButtons.find((button) => button.dataset.cropShape === state.shape)?.focus()
  })
}

async function cropFiles(files) {
  const unique = []
  const seen = new Set()
  for (const file of files) {
    if (!(file instanceof File) || !String(file.type || '').startsWith('image/')) continue
    const key = fileKey(file)
    if (seen.has(key)) continue
    seen.add(key)
    unique.push(file)
  }

  const cropped = []
  for (let index = 0; index < unique.length; index += 1) {
    const result = await createCropDialog(unique[index], index, unique.length)
    if (result) cropped.push(result)
  }
  return cropped
}

export async function cropPhotoFiles(files) {
  return cropFiles(Array.from(files || []))
}

export async function cropPhotoFile(file) {
  if (!(file instanceof File) || !String(file.type || '').startsWith('image/')) {
    throw new Error('Please select a valid image file.')
  }
  return createCropDialog(file, 0, 1)
}

function dispatchProcessedFiles(input, files) {
  const transfer = new DataTransfer()
  files.forEach((file) => transfer.items.add(file))
  input.files = transfer.files
  input.dataset.cropProcessed = 'true'
  input.dispatchEvent(new Event('change', { bubbles: true }))
}

async function processFilesForInput(input, files) {
  if (!files?.length) return
  try {
    const cropped = await cropFiles(Array.from(files))
    if (cropped.length) dispatchProcessedFiles(input, cropped)
  } catch (error) {
    console.error('Photo cropping failed:', error)
    window.alert(error?.message || 'Unable to crop this photo. Please try again.')
  }
}

export function installPhotoAlbumCropUpload() {
  if (typeof document === 'undefined' || globalThis[PHOTO_CROP_UPLOAD_PATCH]) return

  document.addEventListener('change', (event) => {
    const input = event.target
    if (!isPhotoUploadInput(input)) return

    if (input.dataset.cropProcessed === 'true') {
      delete input.dataset.cropProcessed
      return
    }

    const files = Array.from(input.files || [])
    if (!files.length) return
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation?.()
    input.value = ''
    processFilesForInput(input, files)
  }, true)

  document.addEventListener('drop', (event) => {
    const dropZone = event.target?.closest?.('.epa-upload-drop')
    if (!dropZone) return
    const input = dropZone.querySelector('input[type="file"]')
    const files = Array.from(event.dataTransfer?.files || [])
    if (!isPhotoUploadInput(input) || !files.length) return

    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation?.()
    processFilesForInput(input, files)
  }, true)

  globalThis[PHOTO_CROP_UPLOAD_PATCH] = true
}
